import { ExternalLink } from 'lucide-react'
import { Button, Chip } from '@/components/ui'
import type { PipelineRow } from '../api/types'
import { ageLabel, ageVariant } from './stage-meta'

export interface PipelineCardProps {
  row: PipelineRow
  /** Admin or regulatory — gates the inline action buttons. */
  canAct: boolean
  onApprove?: (row: PipelineRow) => void
  onReject?: (row: PipelineRow) => void
  onRetry?: (row: PipelineRow) => void
  /** True while a mutation on this row is in flight. */
  pending?: boolean
}

/**
 * Pipeline card — rendered inside KanbanBoard for each pipeline row.
 *
 * Foundation spec §Archetype 2 Pipeline card:
 *   - identifier (lot name) sans-serif primary, never clipped (regulatory
 *     identifier — wraps to full instead of truncating)
 *   - product name secondary
 *   - metadata: vendor + age + uploader initials
 *   - 1-2 primary actions one-click, role-gated
 *   - click (non-drag) opens detail drawer — handled at the board level
 */
export function PipelineCard({
  row,
  canAct,
  onApprove,
  onReject,
  onRetry,
  pending,
}: PipelineCardProps) {
  const stage = row.stage

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <span
          className="break-words text-sm font-semibold text-primary"
          title={row.lot_name}
        >
          {row.lot_name}
        </span>
        <Chip size="xs" variant={ageVariant(row.age_seconds)} className="flex-shrink-0">
          {ageLabel(row.age_seconds)}
        </Chip>
      </div>
      {row.product_name ? (
        <div className="truncate text-sm text-primary">{row.product_name}</div>
      ) : null}
      <div className="flex items-center gap-2 text-role-metadata">
        <span className="min-w-0 flex-1 truncate">{row.vendor_name ?? '—'}</span>
        {row.uploader ? <span className="truncate">{initials(row.uploader)}</span> : null}
      </div>
      {stage === 'generation_failed' && row.error_message ? (
        <div className="truncate rounded-xs bg-danger-bg px-1.5 py-0.5 text-2xs text-danger-text">
          {row.error_message}
        </div>
      ) : null}

      {stage === 'awaiting_approval' ? (
        <div className="flex items-center gap-1.5 pt-0.5" data-role="card-actions">
          <Button
            variant="primary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onApprove?.(row)
            }}
            disabled={!canAct || pending}
            title={canAct ? undefined : 'Admin or regulatory required'}
          >
            Approve
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onReject?.(row)
            }}
            disabled={!canAct || pending}
            title={canAct ? undefined : 'Admin or regulatory required'}
          >
            Reject
          </Button>
          {row.review_url ? (
            <a
              href={row.review_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="ml-auto inline-flex items-center gap-1 text-2xs text-link hover:underline"
            >
              Review
              <ExternalLink size={10} />
            </a>
          ) : null}
        </div>
      ) : null}

      {stage === 'generation_failed' ? (
        <div className="flex items-center gap-1.5 pt-0.5" data-role="card-actions">
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onRetry?.(row)
            }}
            disabled={!canAct || pending}
            title={canAct ? undefined : 'Admin or regulatory required'}
          >
            Retry
          </Button>
          {row.retry_count > 0 ? (
            <span className="text-role-metadata">
              attempt {row.retry_count + 1}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function initials(email: string): string {
  const local = email.split('@')[0] ?? email
  const parts = local.split(/[._-]/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase() || local.slice(0, 2).toUpperCase()
}
